import axios from 'axios';
import * as Sentry from '@sentry/react';
import { loginDestinationAfterAuthLoss } from '../utils/navigationReturn.js';

// Get API URL from runtime config (config.js) or build-time env var, or use default
// Priority: window.APP_CONFIG.API_BASE_URL > VITE_API_URL > Vite proxy in dev > default
const getApiBaseUrl = () => {
  // First, try runtime configuration (from config.js loaded in index.html)
  if (typeof window !== 'undefined' && window.APP_CONFIG && window.APP_CONFIG.API_BASE_URL) {
    return window.APP_CONFIG.API_BASE_URL;
  }
  
  // In dev, use Vite proxy when VITE_USE_PROXY=true so VITE_PROXY_TARGET is the single config
  if (import.meta.env.DEV && import.meta.env.VITE_USE_PROXY === 'true') {
    return '/api';
  }
  
  // Build-time environment variable (full URL, e.g. http://127.0.0.1:4100)
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }
  
  // Default fallback — IPv4 loopback (Windows localhost often resolves to ::1).
  return 'http://127.0.0.1:4100';
};

const API_BASE_URL = getApiBaseUrl();

/** Full URL for SSE (EventSource); requires token in query because EventSource cannot send Authorization. */
export function getBookingsLivePushEventsUrl(token) {
  const base = String(API_BASE_URL || '').replace(/\/$/, '');
  const qs = new URLSearchParams();
  if (token) qs.set('token', token);
  return `${base}/bookings/live-push-events?${qs.toString()}`;
}

/** SSE URL for resource lock events (optional resourceType / resourceId filters). */
export function getLocksEventsUrl(token, { resourceType, resourceId } = {}) {
  const base = String(API_BASE_URL || '').replace(/\/$/, '');
  const qs = new URLSearchParams();
  if (token) qs.set('token', token);
  if (resourceType) qs.set('resourceType', String(resourceType));
  if (resourceId != null && resourceId !== '') qs.set('resourceId', String(resourceId));
  return `${base}/locks/events?${qs.toString()}`;
}

/** GET /visitors returns `{ visitors, total, … }`; legacy callers expected a bare array. */
export function unwrapVisitorsResponse(data) {
  if (Array.isArray(data)) return data;
  if (data?.visitors && Array.isArray(data.visitors)) return data.visitors;
  return [];
}

/** GET /properties returns `{ items, total, … }`. */
export function unwrapPropertiesResponse(data) {
  if (Array.isArray(data)) return data;
  if (data?.items && Array.isArray(data.items)) return data.items;
  return [];
}

/** Puppeteer PDF / large report exports (override per-request on blob downloads). */
export const REPORT_DOWNLOAD_TIMEOUT_MS = (() => {
  const raw = import.meta.env.VITE_REPORT_DOWNLOAD_TIMEOUT_MS;
  const n = raw != null && raw !== '' ? parseInt(String(raw), 10) : 60000;
  return Number.isFinite(n) && n > 0 ? n : 60000;
})();

/** VAT snapshot generate (CSV overlay months + PDF) — longer than default API 30s. */
export const REPORT_GENERATE_TIMEOUT_MS = (() => {
  const raw = import.meta.env.VITE_REPORT_GENERATE_TIMEOUT_MS;
  const n = raw != null && raw !== '' ? parseInt(String(raw), 10) : 120000;
  return Number.isFinite(n) && n > 0 ? n : 120000;
})();

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 second timeout (increased from 10s)
});

/**
 * Human-readable API target for error messages (e.g. "127.0.0.1:4100" in dev with proxy).
 * @returns {string}
 */
export function getApiTargetForMessage() {
  const base = API_BASE_URL;
  if (base.startsWith('http://') || base.startsWith('https://')) {
    try {
      const u = new URL(base);
      return u.host;
    } catch {
      return base;
    }
  }
  // Relative path (e.g. /api) → proxy target; dev default is 127.0.0.1:4100
  if (import.meta.env.DEV) {
    return import.meta.env.VITE_PROXY_TARGET?.replace(/^https?:\/\//, '') ?? '127.0.0.1:4100';
  }
  return 'API server';
}

/**
 * Check if the API is reachable (GET /health). Used for optional "API not reachable" banner.
 * @param {number} timeoutMs
 * @returns {Promise<{ reachable: boolean }>}
 */
export function checkApiReachable(timeoutMs = 5000) {
  return api.get('/health', { timeout: timeoutMs })
    .then((res) => (res?.status === 200 ? { reachable: true } : { reachable: false }))
    .catch(() => ({ reachable: false }));
}

// Add token to requests if available
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    console.error('[API Request Error]', error);
    return Promise.reject(error);
  }
);

// Retry transient GET failures once or twice (cold proxy, API restart → ECONNRESET, 502 from proxy)
const MAX_RETRIES = 2;
const RETRY_DELAY_MS = 300;

function isRetryableConnectionError(error) {
  if (!error.config || error.config.method?.toUpperCase() !== 'GET') return false;
  const retries = error.config.__retryCount ?? 0;
  if (retries >= MAX_RETRIES) return false;
  if (error.response) {
    return error.response.status === 502;
  }
  const msg = String(error.message || '');
  return (
    error.code === 'ECONNREFUSED' ||
    error.code === 'ECONNRESET' ||
    error.code === 'ETIMEDOUT' ||
    error.code === 'EPIPE' ||
    error.code === 'ERR_NETWORK' ||
    msg.includes('ECONNRESET') ||
    msg.includes('socket hang up') ||
    msg.includes('Network Error')
  );
}

// Handle token expiration and errors
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    // Don't process aborted requests
    if (error.name === 'AbortError' || error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
      return Promise.reject(error);
    }

    // Retry GET requests on connection/502 errors (first-load proxy race)
    if (isRetryableConnectionError(error)) {
      const config = { ...error.config, __retryCount: (error.config.__retryCount ?? 0) + 1 };
      await new Promise((r) => setTimeout(r, RETRY_DELAY_MS));
      return api.request(config);
    }

    const isTimeout = error.code === 'ECONNABORTED' || error.message?.includes('timeout');
    const isNetworkError = !error.response && error.request;
    const is401 = error.response?.status === 401;

    console.error('\n[API Error Interceptor]');
    console.error('Message:', error.message);
    console.error('Code:', error.code);
    console.error('Status:', error.response?.status);
    console.error('Status Text:', error.response?.statusText);
    console.error('URL:', error.config?.baseURL + error.config?.url);
    console.error('Method:', error.config?.method);
    console.error('Request Headers:', error.config?.headers);
    console.error('Request Data:', error.config?.data);
    console.error('Response Headers:', error.response?.headers);
    console.error('Response Data:', error.response?.data);
    console.error('Is Timeout:', isTimeout);
    console.error('Is Network Error:', isNetworkError);
    console.error('Is 401:', is401);
    console.error('========================\n');
    
    // Capture error to Sentry (except for 401 auth errors which are expected)
    if (!is401) {
      Sentry.captureException(error, {
        tags: {
          errorType: isTimeout ? 'timeout' : isNetworkError ? 'network' : 'api',
        },
        extra: {
          url: error.config?.baseURL + error.config?.url,
          method: error.config?.method,
          status: error.response?.status,
          statusText: error.response?.statusText,
        },
      });
    }
    
    // Only logout on actual 401 authentication errors, NOT on timeouts or network errors
    if (is401) {
      // Only logout if we're not already on the login page
      const currentPath = window.location.pathname;
      if (!currentPath.includes('/login')) {
        const errorData = error.response?.data;
        console.error('Unauthorized (401) - Token may have expired or is invalid', {
          message: errorData?.message,
          code: errorData?.code,
          url: error.config?.url,
          method: error.config?.method,
        });
        
        // Check if token exists and log it (for debugging)
        const token = localStorage.getItem('authToken');
        if (token) {
          try {
            const parts = token.split('.');
            if (parts.length === 3) {
              const payload = JSON.parse(atob(parts[1]));
              const now = Math.floor(Date.now() / 1000);
            }
          } catch (e) {
            console.error('Could not decode token:', e);
          }
        }
        
        localStorage.removeItem('authToken');
        // Only redirect if we're not already navigating.
        // Explicit logout sets a flag so this does not resume the previous page.
        if (currentPath !== '/login') {
          window.location.href = loginDestinationAfterAuthLoss({
            pathname: window.location.pathname,
            search: window.location.search,
            hash: window.location.hash,
          });
        }
      }
    } else if (isTimeout) {
      console.error('Request timeout - This is NOT an authentication error. User will NOT be logged out.');
      // Don't logout on timeout errors - just show the error to the user
    } else if (isNetworkError) {
      console.error('Network error - Cannot reach API server. User will NOT be logged out.');
      // Don't logout on network errors either
    }
    
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: async (username, password) => {
    const response = await api.post('/auth/login', { username, password });
    return response.data;
  },
  verifyMfaLogin: async (mfaToken, code) => {
    const response = await api.post('/auth/mfa/verify-login', { mfaToken, code });
    return response.data;
  },
  mfaEnroll: async () => {
    const response = await api.post('/auth/mfa/enroll');
    return response.data;
  },
  mfaConfirm: async (code) => {
    const response = await api.post('/auth/mfa/confirm', { code });
    return response.data;
  },
  mfaDisable: async (code) => {
    const response = await api.post('/auth/mfa/disable', { code });
    return response.data;
  },
  logout: async () => {
    await api.post('/auth/logout');
  },
  forgotPassword: async (emailOrUsername) => {
    const body = /@/.test(emailOrUsername) ? { email: emailOrUsername.trim() } : { username: emailOrUsername.trim() };
    const response = await api.post('/auth/forgot-password', body);
    return response.data;
  },
  resetPassword: async (token, newPassword) => {
    const response = await api.post('/auth/reset-password', { token, newPassword });
    return response.data;
  },
  previewInvite: async (token) => {
    const response = await api.get('/auth/invite', { params: { token } });
    return response.data;
  },
  acceptInvite: async (token, newPassword) => {
    const response = await api.post('/auth/accept-invite', { token, newPassword });
    return response.data;
  },
};

// RBAC access API
export const accessAPI = {
  me: async () => {
    const response = await api.get('/access/me');
    return response.data;
  },
  getOrganizations: async () => {
    const response = await api.get('/access/organizations');
    return response.data;
  },
  getPermissions: async () => {
    const response = await api.get('/access/permissions');
    return response.data;
  },
  getRoles: async () => {
    const response = await api.get('/access/roles');
    return response.data;
  },
  getRole: async (id) => {
    const response = await api.get(`/access/roles/${id}`);
    return response.data;
  },
  createRole: async (payload) => {
    const response = await api.post('/access/roles', payload);
    return response.data;
  },
  updateRole: async (id, payload) => {
    const response = await api.patch(`/access/roles/${id}`, payload);
    return response.data;
  },
  getEmailDigests: async () => {
    const response = await api.get('/access/email-digests');
    return response.data;
  },
  getRoleEmailDigests: async (id) => {
    const response = await api.get(`/access/roles/${id}/email-digests`);
    return response.data;
  },
  updateRoleEmailDigests: async (id, digests) => {
    const response = await api.put(`/access/roles/${id}/email-digests`, { digests });
    return response.data;
  },
  getUserEmailDigests: async (userId) => {
    const response = await api.get(`/access/users/${userId}/email-digests`);
    return response.data;
  },
  updateUserEmailDigests: async (userId, digests) => {
    const response = await api.put(`/access/users/${userId}/email-digests`, { digests });
    return response.data;
  },
  getUserEffective: async (userId) => {
    const response = await api.get(`/access/users/${userId}/effective`);
    return response.data;
  },
  copyAccessFromUser: async (targetUserId, sourceUserId) => {
    const response = await api.post(`/access/users/${targetUserId}/copy-from/${sourceUserId}`);
    return response.data;
  },
};

// Dashboard API
export const dashboardAPI = {
  getTaxData: async (months = 24, { extras = false, scope } = {}) => {
    const response = await api.get(`/dashboard/taxes`, {
      params: { months, extras: extras ? '1' : '0', ...(scope ? { scope } : {}) },
    });
    return response.data;
  },
  
  getDashboard: async () => {
    const response = await api.get('/dashboard');
    return response.data;
  },
  getActivityFeed: async (limit = 50) => {
    const response = await api.get(`/dashboard/activity-feed?limit=${limit}`);
    return response.data;
  },
  getVatHistory: async (period = 'monthly', months = 12) => {
    const response = await api.get(`/dashboard/vat-history?period=${period}&months=${months}`);
    return response.data;
  },
  getComplianceMetrics: async () => {
    const response = await api.get('/dashboard/compliance');
    return response.data;
  },
  getRevenueLeakageAlerts: async () => {
    const response = await api.get('/dashboard/revenue-leakage');
    return response.data;
  },
  getForecast: async (days = 30) => {
    const response = await api.get(`/dashboard/forecast?days=${days}`);
    return response.data;
  },
  // Infrastructure Tax API
  getInfrastructureTax: async () => {
    const response = await api.get('/dashboard/infrastructure-tax');
    return response.data;
  },
  getInfrastructureTaxHistory: async (period = 'monthly', months = 12, { scope } = {}) => {
    const params = new URLSearchParams({ period, months: String(months) });
    if (scope) params.set('scope', scope);
    const response = await api.get(`/dashboard/infrastructure-tax/history?${params.toString()}`);
    return response.data;
  },
  getGuestNightMetrics: async () => {
    const response = await api.get('/dashboard/guest-nights');
    return response.data;
  },
  getInfrastructureTaxForecast: async (days = 30) => {
    const response = await api.get(`/dashboard/infrastructure-tax/forecast?days=${days}`);
    return response.data;
  },
  getInfrastructureTaxProperties: async (limit = 10, offset = 0) => {
    const response = await api.get(`/dashboard/infrastructure-tax/properties?limit=${limit}&offset=${offset}`);
    return response.data;
  },
  getInfrastructureTaxCompliance: async () => {
    const response = await api.get('/dashboard/infrastructure-tax/compliance');
    return response.data;
  },
  // Cross-Tax API
  getCrossTaxMetrics: async () => {
    const response = await api.get('/dashboard/cross-tax/metrics');
    return response.data;
  },
  getCrossTaxAnomalies: async () => {
    const response = await api.get('/dashboard/cross-tax/anomalies');
    return response.data;
  },
  // Regional Map API
  getRegionalData: async () => {
    const response = await api.get('/dashboard/regional');
    return response.data;
  },
};

// Properties API
export const propertiesAPI = {
  getProperties: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.licensed) params.append('licensed', filters.licensed);
    if (filters.type) params.append('type', filters.type);
    if (filters.compliant) params.append('compliant', filters.compliant);
    if (filters.limit) params.append('limit', filters.limit);
    if (filters.offset) params.append('offset', filters.offset);
    if (filters.year != null && filters.year !== '') params.append('year', filters.year);
    if (filters.month != null && filters.month !== '') params.append('month', filters.month);
    
    const response = await api.get(`/properties?${params.toString()}`);
    return response.data;
  },
  /** Unified Properties directory: registry rows + per-hotel metrics (no portfolio totals). */
  getPropertiesDirectory: async (filters = {}, options = {}) => {
    const params = new URLSearchParams();
    if (filters.licensed) params.append('licensed', filters.licensed);
    if (filters.type) params.append('type', filters.type);
    if (filters.compliant) params.append('compliant', filters.compliant);
    if (filters.limit) params.append('limit', filters.limit);
    if (filters.offset) params.append('offset', filters.offset);
    if (filters.year != null && filters.year !== '') params.append('year', filters.year);
    if (filters.month != null && filters.month !== '') params.append('month', filters.month);
    if (filters.includeDuplicateHotels != null && filters.includeDuplicateHotels !== '') {
      params.append('includeDuplicateHotels', String(filters.includeDuplicateHotels));
    }
    if (filters.forLinking != null && filters.forLinking !== '') {
      params.append('forLinking', String(filters.forLinking));
    }
    if (filters.lite != null && filters.lite !== '') {
      params.append('lite', String(filters.lite));
    }
    if (filters.includeMetrics != null && filters.includeMetrics !== '') {
      params.append('includeMetrics', String(filters.includeMetrics));
    }
    if (filters.scope) params.append('scope', filters.scope);
    const response = await api.get(`/properties/directory?${params.toString()}`, {
      signal: options.signal,
      timeout: options.timeout ?? 60000,
    });
    return response.data;
  },
  compareMetrics: async (ids) => {
    const response = await api.post('/properties/compare-metrics', { ids });
    return response.data.metrics;
  },
  getAllHotelsMonthlyTaxSummary: async (year, month, options = {}) => {
    const params = new URLSearchParams();
    params.append('year', String(year));
    params.append('month', String(month));
    const response = await api.get(`/properties/reports/all-hotels-monthly-summary?${params.toString()}`, {
      signal: options.signal,
      timeout: options.timeout ?? 90000,
    });
    return response.data;
  },
  exportAllHotelsMonthlyTaxSummary: async (year, month, fileFormat = 'pdf') => {
    const params = new URLSearchParams();
    params.append('year', String(year));
    params.append('month', String(month));
    const fmt = String(fileFormat || 'pdf').toLowerCase();
    if (fmt !== 'pdf') params.append('format', fmt);
    const response = await api.get(
      `/properties/reports/all-hotels-monthly-summary/export?${params.toString()}`,
      { responseType: 'blob', timeout: REPORT_DOWNLOAD_TIMEOUT_MS }
    );
    return response.data;
  },
  getProperty: async (id) => {
    const response = await api.get(`/properties/${id}`);
    return response.data;
  },
  getPropertyVatReport: async (id, year, month) => {
    const params = new URLSearchParams();
    if (year) params.append('year', year);
    if (month !== undefined) params.append('month', month);
    
    const response = await api.get(`/properties/${id}/vat-report?${params.toString()}`);
    return response.data;
  },
  downloadPropertyVatReport: async (id, year, month, fileFormat = 'pdf') => {
    const params = new URLSearchParams();
    if (year) params.append('year', year);
    if (month !== undefined) params.append('month', month);
    const fmt = String(fileFormat || 'pdf').toLowerCase();
    if (fmt !== 'pdf') params.append('format', fmt);

    const response = await api.get(`/properties/${id}/vat-report/download?${params.toString()}`, {
      responseType: 'blob',
      timeout: REPORT_DOWNLOAD_TIMEOUT_MS,
    });
    return response.data;
  },
  getPropertyVatReports: async (id) => {
    const response = await api.get(`/properties/${id}/vat-reports`);
    return response.data;
  },
  deletePropertyVatReport: async (id, reportId) => {
    const response = await api.delete(`/properties/${id}/vat-reports/${reportId}`);
    return response.data;
  },
  getPropertyVatReportById: async (id, reportId) => {
    const response = await api.get(`/properties/${id}/vat-reports/${reportId}?format=json`);
    return response.data;
  },
  getPropertyRevenueReport: async (id, year, month) => {
    const params = new URLSearchParams();
    if (year) params.append('year', year);
    if (month !== undefined) params.append('month', month);
    const response = await api.get(`/properties/${id}/reports/revenue?${params.toString()}`);
    return response.data;
  },
  getPropertyBookingsReport: async (id, year, month) => {
    const params = new URLSearchParams();
    if (year) params.append('year', year);
    if (month !== undefined) params.append('month', month);
    const response = await api.get(`/properties/${id}/reports/bookings?${params.toString()}`);
    return response.data;
  },
  getPropertyComplianceReport: async (id, year) => {
    const params = new URLSearchParams();
    if (year) params.append('year', year);
    const response = await api.get(`/properties/${id}/reports/compliance?${params.toString()}`);
    return response.data;
  },
  getPropertyAnalyticsReport: async (id, year) => {
    const params = new URLSearchParams();
    if (year) params.append('year', year);
    const response = await api.get(`/properties/${id}/reports/analytics?${params.toString()}`);
    return response.data;
  },
  createProperty: async (property) => {
    const response = await api.post('/properties', property);
    return response.data;
  },
  updateProperty: async (id, property) => {
    const response = await api.put(`/properties/${id}`, property);
    return response.data;
  },
  patchProperty: async (id, updates) => {
    const response = await api.patch(`/properties/${id}`, updates);
    return response.data;
  },
  deleteProperty: async (id) => {
    await api.delete(`/properties/${id}`);
  },
};

// Channels API
export const channelsAPI = {
  getChannels: async (signal) => {
    const response = await api.get('/channels', { signal });
    return response.data;
  },
  getChannel: async (id, signal) => {
    const response = await api.get(`/channels/${id}`, { signal });
    return response.data;
  },
  createChannel: async (channel) => {
    const response = await api.post('/channels', channel);
    return response.data;
  },
  updateChannel: async (id, channel) => {
    const response = await api.put(`/channels/${id}`, channel);
    return response.data;
  },
  patchChannel: async (id, updates) => {
    const response = await api.patch(`/channels/${id}`, updates);
    return response.data;
  },
  deleteChannel: async (id) => {
    await api.delete(`/channels/${id}`);
  },
};

// PMS Suppliers API
export const pmsSuppliersAPI = {
  getPmsSuppliers: async (signal) => {
    const response = await api.get('/pms-suppliers', { signal });
    return response.data;
  },
  getPmsSuppliersWithCounts: async (signal) => {
    const response = await api.get('/pms-suppliers?includeCounts=true', { signal });
    return response.data;
  },
  /** Unique pull+push hotel_codes (deduped) + supplier count for dashboard. */
  getRegistryStats: async (signal) => {
    const response = await api.get('/pms-suppliers/registry-stats', { signal });
    return response.data;
  },
  getPmsSupplier: async (id, signal) => {
    const response = await api.get(`/pms-suppliers/${id}`, { signal });
    return response.data;
  },
  createPmsSupplier: async (supplier) => {
    const response = await api.post('/pms-suppliers', supplier);
    return response.data;
  },
  updatePmsSupplier: async (id, supplier) => {
    const response = await api.put(`/pms-suppliers/${id}`, supplier);
    return response.data;
  },
  patchPmsSupplier: async (id, updates) => {
    const response = await api.patch(`/pms-suppliers/${id}`, updates);
    return response.data;
  },
  deletePmsSupplier: async (id) => {
    await api.delete(`/pms-suppliers/${id}`);
  },
  /** Hotels registered via eZee Zanzibar push API (not pull suppliers). Pass AbortSignal, or { signal?, year?, month? } for revenue aggregates. */
  getPmsPushHotels: async (arg) => {
    const opts =
      arg != null && typeof arg === 'object' && typeof arg.abort === 'function'
        ? { signal: arg }
        : arg && typeof arg === 'object'
          ? { ...arg }
          : {};
    const { signal, year, month, includeMetrics } = opts;
    const params = {};
    if (year != null && month != null) {
      params.year = year;
      params.month = month;
    }
    if (includeMetrics) {
      params.includeMetrics = '1';
    }
    const response = await api.get('/pms-suppliers/push-hotels', { signal, params });
    return response.data;
  },
  updatePmsPushHotel: async (supplierId, hotelId, updates) => {
    const response = await api.put(`/pms-suppliers/${supplierId}/push-hotels/${hotelId}`, updates);
    return response.data;
  },
  deletePmsPushHotel: async (supplierId, hotelId) => {
    await api.delete(`/pms-suppliers/${supplierId}/push-hotels/${hotelId}`);
  },
  getPushApiKeys: async (supplierId, signal) => {
    const response = await api.get(`/pms-suppliers/${supplierId}/push-api-keys`, signal ? { signal } : {});
    return response.data;
  },
  setPushApiKeyAssignment: async (supplierId, keyId, assigned) => {
    const response = await api.patch(`/pms-suppliers/${supplierId}/push-api-keys/${keyId}`, { assigned });
    return response.data;
  },
  /** Favicon-sized logo PNG (16 | 32 | 64). Pass `v` (e.g. logoUpdatedAt ms) to bust caches after replace. */
  getLogoBlob: async (supplierId, size = 32, options = {}) => {
    const opts =
      options != null && typeof options === 'object' && typeof options.abort === 'function'
        ? { signal: options }
        : options && typeof options === 'object'
          ? options
          : {};
    const { signal, v } = opts;
    const params = { size };
    if (v != null && v !== '') params.v = String(v);
    const response = await api.get(`/pms-suppliers/${supplierId}/logo`, {
      params,
      responseType: 'blob',
      signal,
      headers: {
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
      },
    });
    return response.data;
  },
  uploadLogo: async (supplierId, file) => {
    const form = new FormData();
    form.append('file', file);
    const response = await api.post(`/pms-suppliers/${supplierId}/logo`, form, {
      transformRequest: [
        (data, headers) => {
          if (headers && typeof headers === 'object') {
            delete headers['Content-Type'];
          }
          return data;
        },
      ],
    });
    return response.data;
  },
  deleteLogo: async (supplierId) => {
    const response = await api.delete(`/pms-suppliers/${supplierId}/logo`);
    return response.data;
  },
};

/** CSV import hotels — read-only list for authenticated users (PMS Suppliers directory). Admin CRUD: pmsCsvHotelsAPI under /admin. */
export async function getPmsCsvHotelsDirectoryList(signal) {
  const response = await api.get('/pms-csv-hotels', { signal });
  return response.data;
}

export const pmsHotelsAPI = {
  getPmsHotels: async (supplierId, opts = {}) => {
    const { signal, year, month, includeMetrics } = opts;
    const params = new URLSearchParams();
    if (year != null) params.set('year', String(year));
    if (month != null) params.set('month', String(month));
    if (includeMetrics) params.set('includeMetrics', '1');
    const qs = params.toString();
    const url = `/pms-suppliers/${supplierId}/hotels${qs ? `?${qs}` : ''}`;
    const response = await api.get(url, { signal });
    return response.data;
  },
  getAllPmsPullHotels: async (opts = {}) => {
    const { signal, year, month, includeMetrics } = opts;
    const params = new URLSearchParams();
    if (year != null) params.set('year', String(year));
    if (month != null) params.set('month', String(month));
    if (includeMetrics) params.set('includeMetrics', '1');
    const qs = params.toString();
    const url = `/pms-suppliers/all-hotels${qs ? `?${qs}` : ''}`;
    const response = await api.get(url, { signal });
    return response.data;
  },
  getPmsPullHotelCount: async (supplierId, signal) => {
    const response = await api.get(`/pms-suppliers/${supplierId}/hotels/count`, { signal });
    return response.data?.count ?? 0;
  },
  getSyncFailures: async (supplierId, opts = {}) => {
    const { signal, limit } = opts;
    const params = new URLSearchParams();
    if (limit != null) params.set('limit', String(limit));
    const qs = params.toString();
    const response = await api.get(
      `/pms-suppliers/${supplierId}/hotels/sync-failures${qs ? `?${qs}` : ''}`,
      { signal }
    );
    return response.data;
  },
  getPmsHotel: async (supplierId, hotelCode, signal) => {
    const response = await api.get(`/pms-suppliers/${supplierId}/hotels/${hotelCode}`, { signal });
    return response.data;
  },
  getPmsHotelSummary: async (supplierId, hotelCode, year, month) => {
    const params = new URLSearchParams();
    if (year != null) params.set('year', String(year));
    if (month != null) params.set('month', String(month));
    const response = await api.get(`/pms-suppliers/${supplierId}/hotels/${hotelCode}/summary?${params.toString()}`);
    return response.data;
  },
  getPmsHotelPeriodSummary: async (supplierId, hotelCode, startYear, startMonth) => {
    const params = new URLSearchParams();
    if (startYear != null) params.set('startYear', String(startYear));
    if (startMonth != null) params.set('startMonth', String(startMonth));
    const response = await api.get(
      `/pms-suppliers/${supplierId}/hotels/${hotelCode}/reports/period-summary?${params.toString()}`
    );
    return response.data;
  },
  exportPmsHotelPeriodSummary: async (supplierId, hotelCode, startYear, startMonth, format) => {
    const params = new URLSearchParams();
    if (startYear != null) params.set('startYear', String(startYear));
    if (startMonth != null) params.set('startMonth', String(startMonth));
    params.set('format', format);
    const response = await api.get(
      `/pms-suppliers/${supplierId}/hotels/${hotelCode}/reports/period-summary/export?${params.toString()}`,
      { responseType: 'blob', timeout: REPORT_DOWNLOAD_TIMEOUT_MS }
    );
    return response;
  },
  getPmsHotelOverview: async (supplierId, hotelCode, year, month) => {
    const params = new URLSearchParams();
    if (year != null) params.set('year', String(year));
    if (month != null) params.set('month', String(month));
    const response = await api.get(`/pms-suppliers/${supplierId}/hotels/${hotelCode}/overview?${params.toString()}`);
    return response.data;
  },
  createPmsHotel: async (supplierId, hotel) => {
    const response = await api.post(`/pms-suppliers/${supplierId}/hotels`, hotel);
    return response.data;
  },
  updatePmsHotel: async (supplierId, hotelCode, hotel) => {
    const response = await api.put(`/pms-suppliers/${supplierId}/hotels/${hotelCode}`, hotel);
    return response.data;
  },
  refreshPmsHotelProfile: async (supplierId, hotelId) => {
    const response = await api.post(
      `/pms-suppliers/${supplierId}/hotels/${hotelId}/refresh-profile`
    );
    return response.data;
  },
  enrichPmsHotelDetails: async (supplierId, hotelId) => {
    const response = await api.post(
      `/pms-suppliers/${supplierId}/hotels/${hotelId}/enrich-details`
    );
    return response.data;
  },
  patchPmsHotel: async (supplierId, hotelCode, updates) => {
    const response = await api.patch(`/pms-suppliers/${supplierId}/hotels/${hotelCode}`, updates);
    return response.data;
  },
  deletePmsHotel: async (supplierId, hotelCode) => {
    await api.delete(`/pms-suppliers/${supplierId}/hotels/${hotelCode}`);
  },
  getPmsHotelVatReport: async (supplierId, hotelCode, year, month, opts = {}) => {
    const params = new URLSearchParams();
    if (opts.from && opts.to) {
      params.append('from', opts.from);
      params.append('to', opts.to);
    } else {
      if (year) params.append('year', year);
      if (month !== undefined) params.append('month', month);
    }
    if (opts.zeroValuePast) params.append('zeroValuePast', '1');

    const response = await api.get(`/pms-suppliers/${supplierId}/hotels/${hotelCode}/vat-report?${params.toString()}`);
    return response.data;
  },
  downloadPmsHotelVatReport: async (supplierId, hotelCode, year, month, fileFormat = 'pdf', opts = {}) => {
    const params = new URLSearchParams();
    if (opts.from && opts.to) {
      params.append('from', opts.from);
      params.append('to', opts.to);
    } else {
      if (year) params.append('year', year);
      if (month !== undefined) params.append('month', month);
    }
    const fmt = String(fileFormat || 'pdf').toLowerCase();
    if (fmt !== 'pdf') params.append('format', fmt);
    if (opts.zeroValuePast) params.append('zeroValuePast', '1');

    const response = await api.get(`/pms-suppliers/${supplierId}/hotels/${hotelCode}/vat-report/download?${params.toString()}`, {
      responseType: 'blob',
      timeout: REPORT_DOWNLOAD_TIMEOUT_MS,
    });
    return response.data;
  },
  getPmsHotelVatReports: async (supplierId, hotelCode) => {
    const response = await api.get(`/pms-suppliers/${supplierId}/hotels/${hotelCode}/vat-reports`);
    return response.data;
  },
  deletePmsHotelVatReport: async (supplierId, hotelCode, reportId) => {
    const response = await api.delete(
      `/pms-suppliers/${supplierId}/hotels/${hotelCode}/vat-reports/${reportId}`
    );
    return response.data;
  },
  generatePmsHotelVatReport: async (supplierId, hotelCode, year, month) => {
    const response = await api.post(
      `/pms-suppliers/${supplierId}/hotels/${hotelCode}/vat-reports/generate`,
      { year, month },
      { timeout: REPORT_GENERATE_TIMEOUT_MS }
    );
    return response.data;
  },
  getPmsHotelVatReportById: async (supplierId, hotelCode, reportId) => {
    const response = await api.get(
      `/pms-suppliers/${supplierId}/hotels/${hotelCode}/vat-reports/${reportId}?format=json`
    );
    return response.data;
  },
  getPmsHotelRevenueReport: async (supplierId, hotelCode, year, month, opts = {}) => {
    const params = new URLSearchParams();
    if (year) params.append('year', year);
    if (month !== undefined) params.append('month', month);
    const response = await api.get(
      `/pms-suppliers/${supplierId}/hotels/${hotelCode}/reports/revenue?${params.toString()}`,
      { signal: opts.signal }
    );
    return response.data;
  },
  getPmsHotelBookingsReport: async (supplierId, hotelCode, opts = {}) => {
    const params = new URLSearchParams();
    if (opts.year != null) params.append('year', opts.year);
    if (opts.month !== undefined) params.append('month', opts.month);
    if (opts.from) params.append('from', opts.from);
    if (opts.to) params.append('to', opts.to);
    if (opts.status) params.append('status', opts.status);
    if (opts.zeroValuePast) params.append('zeroValuePast', '1');
    const response = await api.get(`/pms-suppliers/${supplierId}/hotels/${hotelCode}/reports/bookings?${params.toString()}`);
    return response.data;
  },
  getPmsHotelBookingsReportExport: async (supplierId, hotelCode, format, opts = {}) => {
    const params = new URLSearchParams();
    params.set('format', format);
    if (opts.year != null) params.append('year', opts.year);
    if (opts.month !== undefined) params.append('month', opts.month);
    if (opts.from) params.append('from', opts.from);
    if (opts.to) params.append('to', opts.to);
    if (opts.status) params.append('status', opts.status);
    if (opts.zeroValuePast) params.append('zeroValuePast', '1');
    const response = await api.get(
      `/pms-suppliers/${supplierId}/hotels/${hotelCode}/reports/bookings/export?${params.toString()}`,
      { responseType: 'blob', timeout: REPORT_DOWNLOAD_TIMEOUT_MS }
    );
    return response.data;
  },
  getPmsHotelComplianceReport: async (supplierId, hotelCode, year) => {
    const params = new URLSearchParams();
    if (year) params.append('year', year);
    const response = await api.get(`/pms-suppliers/${supplierId}/hotels/${hotelCode}/reports/compliance?${params.toString()}`);
    return response.data;
  },
  getPmsHotelAnalyticsReport: async (supplierId, hotelCode, year) => {
    const params = new URLSearchParams();
    if (year) params.append('year', year);
    const response = await api.get(`/pms-suppliers/${supplierId}/hotels/${hotelCode}/reports/analytics?${params.toString()}`);
    return response.data;
  },
  getPmsHotelBookingEntryAnomaly: async (supplierId, hotelCode, month) => {
    const params = new URLSearchParams();
    params.set('month', month);
    const response = await api.get(`/pms-suppliers/${supplierId}/hotels/${hotelCode}/booking-entry-anomalies?${params.toString()}`);
    return response.data;
  },
  /** @param {{ by?: 'checkin' | 'reservation_date' | 'entered_hour' }} [opts] */
  getPmsHotelBookingEntryAnomalyDaily: async (supplierId, hotelCode, month, opts = {}) => {
    const params = new URLSearchParams();
    params.set('month', month);
    if (opts.by === 'reservation_date' || opts.by === 'entered_hour' || opts.by === 'checkin') {
      params.set('by', opts.by);
    }
    const response = await api.get(`/pms-suppliers/${supplierId}/hotels/${hotelCode}/booking-entry-anomalies/daily?${params.toString()}`);
    return response.data;
  },
  getPmsHotelBookingMonthDateCoverage: async (supplierId, hotelCode, month) => {
    const params = new URLSearchParams();
    params.set('month', month);
    const response = await api.get(
      `/pms-suppliers/${supplierId}/hotels/${hotelCode}/booking-entry-anomalies/month-date-coverage?${params.toString()}`
    );
    return response.data;
  },
};

// Bookings API
export const bookingsAPI = {
  getBookings: async (filters = {}, axiosConfig = {}) => {
    const params = new URLSearchParams();
    if (filters.from) params.append('from', filters.from);
    if (filters.to) params.append('to', filters.to);
    if (filters.recurrent !== undefined) params.append('recurrent', filters.recurrent);
    if (filters.limit) params.append('limit', filters.limit);
    if (filters.offset !== undefined) params.append('offset', filters.offset);
    if (filters.search) params.append('search', filters.search);
    if (filters.hotelCode != null && filters.hotelCode !== '') params.append('hotelCode', filters.hotelCode);
    if (filters.status) params.append('status', filters.status);
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);
    if (filters.ids != null && filters.ids !== '') {
      const ids =
        Array.isArray(filters.ids) ? filters.ids.join(',') : String(filters.ids);
      if (ids.trim()) params.append('ids', ids.trim());
    }
    if (filters.zeroValuePast) params.append('zeroValuePast', '1');

    const response = await api.get(`/bookings?${params.toString()}`, {
      headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
      ...axiosConfig,
    });
    return response.data;
  },
  getBooking: async (id) => {
    const response = await api.get(`/bookings/${id}`);
    return response.data;
  },
  getBookingSourceRevisions: async (id) => {
    const response = await api.get(`/bookings/${id}/source-revisions`);
    return response.data;
  },
  getBookingRevisionReceivedPayload: async (bookingId, revisionId) => {
    const response = await api.get(
      `/bookings/${bookingId}/source-revisions/${revisionId}/received-payload`
    );
    return response.data;
  },
  getGuestHistory: async (guestEmail, hotelCode = null, excludeBookingId = null) => {
    const params = new URLSearchParams();
    if (guestEmail) params.set('guestEmail', guestEmail);
    if (hotelCode != null && hotelCode !== '') params.set('hotelId', hotelCode);
    if (excludeBookingId) params.set('excludeBookingId', excludeBookingId);
    const response = await api.get(`/bookings/guest-history?${params.toString()}`);
    return response.data;
  },
  getBookingHotels: async (search = '', limit = 50, axiosConfig = {}) => {
    const params = new URLSearchParams();
    if (search && search.trim()) params.set('search', search.trim());
    if (limit != null) params.set('limit', String(limit));
    const codes = axiosConfig.codes;
    if (Array.isArray(codes) && codes.length > 0) {
      params.set('codes', codes.map((c) => String(c).trim()).filter(Boolean).join(','));
    }
    const { codes: _codes, ...restConfig } = axiosConfig;
    const response = await api.get(`/bookings/hotels?${params.toString()}`, restConfig);
    return response.data;
  },
  createBooking: async (booking) => {
    const response = await api.post('/bookings', booking);
    return response.data;
  },
  updateBooking: async (id, booking) => {
    const response = await api.put(`/bookings/${id}`, booking);
    return response.data;
  },
  patchBooking: async (id, updates) => {
    const response = await api.patch(`/bookings/${id}`, updates);
    return response.data;
  },
  deleteBooking: async (id) => {
    await api.delete(`/bookings/${id}`);
  },
};

// Visitors API
export const visitorsAPI = {
  getVisitors: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.limit) params.append('limit', filters.limit);
    if (filters.offset) params.append('offset', filters.offset);
    if (filters.search) params.append('search', filters.search);
    const response = await api.get(`/visitors?${params.toString()}`);
    return response.data;
  },
  getVisitor: async (id) => {
    const response = await api.get(`/visitors/${id}`);
    return response.data;
  },
  createVisitor: async (visitor) => {
    const response = await api.post('/visitors', visitor);
    return response.data;
  },
  updateVisitor: async (id, visitor) => {
    const response = await api.put(`/visitors/${id}`, visitor);
    return response.data;
  },
  patchVisitor: async (id, updates) => {
    const response = await api.patch(`/visitors/${id}`, updates);
    return response.data;
  },
  deleteVisitor: async (id) => {
    await api.delete(`/visitors/${id}`);
  },
};

// Search API
export const searchAPI = {
  search: async (query, limit = 25, offset = 0) => {
    const response = await api.get(`/search?q=${encodeURIComponent(query)}&limit=${limit}&offset=${offset}`);
    return response.data;
  },
};

// Users API (Admin only)
export const usersAPI = {
  getUsers: async (params = {}) => {
    const response = await api.get('/users', { params });
    return response.data;
  },
  getUser: async (id) => {
    const response = await api.get(`/users/${id}`);
    return response.data;
  },
  createUser: async (user) => {
    const response = await api.post('/users', user);
    return response.data;
  },
  updateUser: async (id, user) => {
    const response = await api.put(`/users/${id}`, user);
    return response.data;
  },
  resendInvite: async (id) => {
    const response = await api.post(`/users/${id}/resend-invite`);
    return response.data;
  },
  deleteUser: async (id) => {
    await api.delete(`/users/${id}/permanent`);
  },
  /** Soft-deactivate (legacy DELETE /users/:id). Prefer updateUser({ status }) from the UI. */
  deactivateUser: async (id) => {
    await api.delete(`/users/${id}`);
  },
};

/** Generic exclusive edit locks (resourceType + resourceId). */
export const locksAPI = {
  acquire: async (resourceType, resourceId) => {
    try {
      const response = await api.post('/locks', { resourceType, resourceId: String(resourceId) });
      return response.data;
    } catch (err) {
      if (err.response?.status === 409 && err.response?.data) {
        return err.response.data;
      }
      throw err;
    }
  },
  heartbeat: async (resourceType, resourceId, sessionId) => {
    const response = await api.post('/locks/heartbeat', {
      resourceType,
      resourceId: String(resourceId),
      sessionId,
    });
    return response.data;
  },
  release: async (resourceType, resourceId, sessionId) => {
    const response = await api.delete(
      `/locks/${encodeURIComponent(resourceType)}/${encodeURIComponent(String(resourceId))}`,
      { params: { sessionId } }
    );
    return response.data;
  },
  getStatus: async (resourceType, resourceId) => {
    const response = await api.get(
      `/locks/${encodeURIComponent(resourceType)}/${encodeURIComponent(String(resourceId))}`
    );
    return response.data;
  },
};

/** Best-effort lock release on tab close (EventSource/axios may not finish). */
export function postLockReleaseKeepalive(resourceType, resourceId, sessionId) {
  const token = typeof localStorage !== 'undefined' ? localStorage.getItem('authToken') : null;
  if (!token || !sessionId || resourceType == null || resourceId == null) return;
  const base = String(API_BASE_URL || '').replace(/\/$/, '');
  const url = `${base}/locks/${encodeURIComponent(resourceType)}/${encodeURIComponent(String(resourceId))}?sessionId=${encodeURIComponent(sessionId)}`;
  try {
    fetch(url, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
      keepalive: true,
    }).catch(() => {});
  } catch {
    // ignore
  }
}

// Settings API (Admin only)
export const settingsAPI = {
  getSettings: async (signal) => {
    const response = await api.get('/settings', signal ? { signal } : {});
    return response.data;
  },
  getSetting: async (key) => {
    const response = await api.get(`/settings/${key}`);
    return response.data;
  },
  setSetting: async (key, value, description = null) => {
    const response = await api.put(`/settings/${key}`, { value, description });
    return response.data;
  },
  deleteSetting: async (key) => {
    await api.delete(`/settings/${key}`);
  },
  getSecuritySettings: async () => {
    const response = await api.get('/settings/security');
    return response.data;
  },
  updateSecuritySettings: async (data) => {
    const response = await api.put('/settings/security', data);
    return response.data;
  },
};

// Admin API (Admin only - maintenance tasks)
export const adminAPI = {
  getTaxSummaryRebuildStatus: async () => {
    const response = await api.get('/admin/rebuild-tax/status');
    return response.data;
  },
  rebuildTaxSummaries: async () => {
    // Returns 202 immediately; rebuild runs year-by-year in the background.
    const response = await api.post('/admin/rebuild-tax', {}, { timeout: 30000 });
    return response.data;
  },
  cancelTaxSummaryRebuild: async () => {
    const response = await api.post('/admin/rebuild-tax/cancel', {}, { timeout: 30000 });
    return response.data;
  },
  getDashboardKpiRebuildStatus: async () => {
    const response = await api.get('/admin/rebuild-dashboard-kpis/status');
    return response.data;
  },
  rebuildDashboardKpis: async () => {
    const response = await api.post('/admin/rebuild-dashboard-kpis', {}, { timeout: 150000 });
    return response.data;
  },
  getExchangeRateStatus: async ({ from, to } = {}) => {
    const params = {};
    if (from) params.from = from;
    if (to) params.to = to;
    const response = await api.get('/admin/exchange-rates/status', { params });
    return response.data;
  },
  previewExchangeRateHistorical: async ({ from, to, skipExisting = true }) => {
    const response = await api.get('/admin/exchange-rates/preview', {
      params: { from, to, skipExisting: skipExisting ? 'true' : 'false' },
    });
    return response.data;
  },
  fetchLatestExchangeRates: async () => {
    const response = await api.post('/admin/fetch-exchange-rates');
    return response.data;
  },
  fetchHistoricalExchangeRates: async ({ fromDate, toDate, skipExisting = true }) => {
    const response = await api.post(
      '/admin/fetch-exchange-rates/historical',
      { fromDate, toDate, skipExisting },
      { timeout: 300000 }
    );
    return response.data;
  },
  getSystemStatus: async () => {
    const response = await api.get('/admin/system-status');
    return response.data;
  },
  getNginxStatus: async () => {
    const response = await api.get('/admin/nginx-status');
    return response.data;
  },
};

/** Admin: VAT report lifecycle / staleness monitor */
export const vatReportControlsAPI = {
  monitor: async () => {
    const response = await api.get('/admin/vat-report-controls/monitor');
    return response.data;
  },
  detectStale: async ({ sendDigest = true, limit } = {}) => {
    const response = await api.post('/admin/vat-report-controls/detect-stale', {
      sendDigest,
      limit,
    });
    return response.data;
  },
  sendOutdatedDigest: async () => {
    const response = await api.post('/admin/vat-report-controls/send-outdated-digest');
    return response.data;
  },
};

/** Admin: duplicate shadow hotels vs canonical comparison + property-code merge */
export const duplicateHotelsAdminAPI = {
  getOverview: async ({ year, month }) => {
    const params = {};
    if (year != null) params.year = String(year);
    if (month != null) params.month = String(month);
    const response = await api.get('/admin/duplicate-hotels', { params });
    return response.data;
  },
  listMerges: async () => {
    const response = await api.get('/admin/property-code-merge');
    return response.data;
  },
  previewMerge: async (body) => {
    const response = await api.post('/admin/property-code-merge/preview', body);
    return response.data;
  },
  applyMerge: async (body) => {
    const response = await api.post('/admin/property-code-merge', body);
    return response.data;
  },
  removeMerge: async (body) => {
    const response = await api.delete('/admin/property-code-merge', { data: body });
    return response.data;
  },
};

/** Admin: commercial entities (billing/registry clients) */
export const commercialEntitiesAPI = {
  list: async () => {
    const response = await api.get('/commercial-entities');
    return response.data;
  },
  get: async (id) => {
    const response = await api.get(`/commercial-entities/${id}`);
    return response.data;
  },
  create: async (body) => {
    const response = await api.post('/commercial-entities', body);
    return response.data;
  },
  patch: async (id, body) => {
    const response = await api.patch(`/commercial-entities/${id}`, body);
    return response.data;
  },
  remove: async (id) => {
    await api.delete(`/commercial-entities/${id}`);
  },
};

/** Admin: legal entities (hotel owners / ZTN registry) — legacy CRUD; prefer taxpayersAPI for CRM */
export const legalEntitiesAPI = {
  list: async () => {
    const response = await api.get('/legal-entities');
    return response.data;
  },
  get: async (id) => {
    const response = await api.get(`/legal-entities/${id}`);
    return response.data;
  },
  create: async (body) => {
    const response = await api.post('/legal-entities', body);
    return response.data;
  },
  patch: async (id, body) => {
    const response = await api.patch(`/legal-entities/${id}`, body);
    return response.data;
  },
  remove: async (id) => {
    await api.delete(`/legal-entities/${id}`);
  },
  previewImport: async (csvText) => {
    const response = await api.post('/legal-entities/import/preview', { csvText });
    return response.data;
  },
  commitImport: async (selections) => {
    const response = await api.post('/legal-entities/import/commit', { selections });
    return response.data;
  },
};

/** Property CRM (unified registry CRM at /property-crm) */
export const propertyCrmAPI = {
  meta: async () => {
    const response = await api.get('/property-crm/meta');
    return response.data;
  },
  list: async (params = {}) => {
    const response = await api.get('/property-crm', { params });
    return response.data;
  },
  get: async (registry, id) => {
    const response = await api.get(`/property-crm/${registry}/${id}`);
    return response.data;
  },
  patch: async (registry, id, body) => {
    const response = await api.patch(`/property-crm/${registry}/${id}`, body);
    return response.data;
  },
  listAuditors: async () => {
    const response = await api.get('/property-crm/auditors');
    return response.data;
  },
  myAssignments: async () => {
    const response = await api.get('/property-crm/my-assignments');
    return response.data;
  },
  setTaxRegime: async (registry, id, body) => {
    const response = await api.post(`/property-crm/${registry}/${id}/tax-regime`, body);
    return response.data;
  },
  setTaxpayer: async (registry, id, body) => {
    const response = await api.post(`/property-crm/${registry}/${id}/taxpayer`, body);
    return response.data;
  },
  listRooms: async (registry, id) => {
    const response = await api.get(`/property-crm/${registry}/${id}/rooms`);
    return response.data;
  },
  createRoom: async (registry, id, body) => {
    const response = await api.post(`/property-crm/${registry}/${id}/rooms`, body);
    return response.data;
  },
  patchRoom: async (registry, id, roomId, body) => {
    const response = await api.patch(`/property-crm/${registry}/${id}/rooms/${roomId}`, body);
    return response.data;
  },
  deleteRoom: async (registry, id, roomId) => {
    await api.delete(`/property-crm/${registry}/${id}/rooms/${roomId}`);
  },
  bookingSources: async (registry, id, params = {}) => {
    const response = await api.get(`/property-crm/${registry}/${id}/booking-sources`, { params });
    return response.data;
  },
  listOtas: async (registry, id) => {
    const response = await api.get(`/property-crm/${registry}/${id}/otas`);
    return response.data;
  },
  createOta: async (registry, id, body) => {
    const response = await api.post(`/property-crm/${registry}/${id}/otas`, body);
    return response.data;
  },
  patchOta: async (registry, id, otaId, body) => {
    const response = await api.patch(`/property-crm/${registry}/${id}/otas/${otaId}`, body);
    return response.data;
  },
  deleteOta: async (registry, id, otaId) => {
    await api.delete(`/property-crm/${registry}/${id}/otas/${otaId}`);
  },
  listContacts: async (registry, id) => {
    const response = await api.get(`/property-crm/${registry}/${id}/contacts`);
    return response.data;
  },
  createContact: async (registry, id, body) => {
    const response = await api.post(`/property-crm/${registry}/${id}/contacts`, body);
    return response.data;
  },
  patchContact: async (registry, id, contactId, body) => {
    const response = await api.patch(`/property-crm/${registry}/${id}/contacts/${contactId}`, body);
    return response.data;
  },
  deleteContact: async (registry, id, contactId) => {
    await api.delete(`/property-crm/${registry}/${id}/contacts/${contactId}`);
  },
  listActivities: async (registry, id, params = {}) => {
    const response = await api.get(`/property-crm/${registry}/${id}/activities`, { params });
    return response.data;
  },
  createActivity: async (registry, id, body) => {
    const response = await api.post(`/property-crm/${registry}/${id}/activities`, body);
    return response.data;
  },
  listIntegrationEvents: async (registry, id) => {
    const response = await api.get(`/property-crm/${registry}/${id}/integration-events`);
    return response.data;
  },
  createIntegrationEvent: async (registry, id, body) => {
    const response = await api.post(`/property-crm/${registry}/${id}/integration-events`, body);
    return response.data;
  },
  listTasks: async (registry, id) => {
    const response = await api.get(`/property-crm/${registry}/${id}/tasks`);
    return response.data;
  },
  getReportLinks: async (registry, id) => {
    const response = await api.get(`/property-crm/${registry}/${id}/report-links`);
    return response.data;
  },
};

/** Taxpayers CRM (enriched LegalEntity registry at /taxpayers) */
export const taxpayersAPI = {
  list: async (params = {}) => {
    const response = await api.get('/taxpayers', { params });
    return response.data;
  },
  get: async (id) => {
    const response = await api.get(`/taxpayers/${id}`);
    return response.data;
  },
  create: async (body) => {
    const response = await api.post('/taxpayers', body);
    return response.data;
  },
  patch: async (id, body) => {
    const response = await api.patch(`/taxpayers/${id}`, body);
    return response.data;
  },
  checkDuplicates: async (body) => {
    const response = await api.post('/taxpayers/duplicates/check', body);
    return response.data;
  },
  listProperties: async (id) => {
    const response = await api.get(`/taxpayers/${id}/properties`);
    return response.data;
  },
  linkProperty: async (id, body) => {
    const response = await api.post(`/taxpayers/${id}/properties/link`, body);
    return response.data;
  },
  unlinkProperty: async (id, body) => {
    const response = await api.post(`/taxpayers/${id}/properties/unlink`, body);
    return response.data;
  },
  listContacts: async (id) => {
    const response = await api.get(`/taxpayers/${id}/contacts`);
    return response.data;
  },
  createContact: async (id, body) => {
    const response = await api.post(`/taxpayers/${id}/contacts`, body);
    return response.data;
  },
  patchContact: async (id, contactId, body) => {
    const response = await api.patch(`/taxpayers/${id}/contacts/${contactId}`, body);
    return response.data;
  },
  removeContact: async (id, contactId) => {
    await api.delete(`/taxpayers/${id}/contacts/${contactId}`);
  },
  listActivities: async (id, params = {}) => {
    const response = await api.get(`/taxpayers/${id}/activities`, { params });
    return response.data;
  },
  createActivity: async (id, body) => {
    const response = await api.post(`/taxpayers/${id}/activities`, body);
    return response.data;
  },
  listTasks: async (id) => {
    const response = await api.get(`/taxpayers/${id}/tasks`);
    return response.data;
  },
  merge: async (id, { sourceId }) => {
    const response = await api.post('/taxpayers/merge', { targetId: id, sourceId });
    return response.data;
  },
  getReport: async (id, params = {}) => {
    const response = await api.get(`/taxpayers/${id}/report`, { params });
    return response.data;
  },
  exportReport: async (id, { format = 'pdf', from, to } = {}) => {
    const fmt = String(format || 'pdf').toLowerCase();
    const path = fmt === 'excel' || fmt === 'xlsx' ? 'excel' : 'pdf';
    const params = {};
    if (from) params.from = from;
    if (to) params.to = to;
    const response = await api.get(`/taxpayers/${id}/report/${path}`, {
      params,
      responseType: 'blob',
      timeout: REPORT_DOWNLOAD_TIMEOUT_MS,
    });
    return response.data;
  },
  /** CSV import aliases (same as legalEntitiesAPI; prefers /taxpayers/import/*) */
  previewImport: async (csvText) => {
    const response = await api.post('/taxpayers/import/preview', { csvText });
    return response.data;
  },
  commitImport: async (selections) => {
    const response = await api.post('/taxpayers/import/commit', { selections });
    return response.data;
  },
  importPreview: async (csvText) => taxpayersAPI.previewImport(csvText),
  importCommit: async (selections) => taxpayersAPI.commitImport(selections),
};

/** Admin: FTO (foreign tour operator) entities and source alias mapping */
export const ftoEntitiesAPI = {
  list: async (params = {}) => {
    const response = await api.get('/fto-entities', { params });
    return response.data;
  },
  get: async (id) => {
    const response = await api.get(`/fto-entities/${id}`);
    return response.data;
  },
  create: async (body) => {
    const response = await api.post('/fto-entities', body);
    return response.data;
  },
  patch: async (id, body) => {
    const response = await api.patch(`/fto-entities/${id}`, body);
    return response.data;
  },
  remove: async (id) => {
    await api.delete(`/fto-entities/${id}`);
  },
  unmappedSources: async (params = {}) => {
    const response = await api.get('/fto-entities/unmapped-sources', { params });
    return response.data;
  },
  mappedSources: async (params = {}) => {
    const response = await api.get('/fto-entities/mapped-sources', { params });
    return response.data;
  },
  complianceReport: async (params = {}) => {
    const response = await api.get('/fto-entities/compliance-report', { params });
    return response.data;
  },
  listAliases: async (entityId) => {
    const response = await api.get(`/fto-entities/${entityId}/aliases`);
    return response.data;
  },
  createAlias: async (entityId, body) => {
    const response = await api.post(`/fto-entities/${entityId}/aliases`, body);
    return response.data;
  },
  patchAlias: async (entityId, aliasId, body) => {
    const response = await api.patch(`/fto-entities/${entityId}/aliases/${aliasId}`, body);
    return response.data;
  },
  removeAlias: async (entityId, aliasId) => {
    await api.delete(`/fto-entities/${entityId}/aliases/${aliasId}`);
  },
};

// Admin: CSV format sources (PMS/OTA) + column mappings
export const pmsCsvSourcesAPI = {
  list: async () => {
    const response = await api.get('/admin/pms-csv-sources');
    return response.data;
  },
  get: async (id) => {
    const response = await api.get(`/admin/pms-csv-sources/${id}`);
    return response.data;
  },
  getCanonicalFields: async () => {
    const response = await api.get('/admin/pms-csv-sources/canonical-fields');
    return response.data;
  },
  getValueSanitizePresets: async () => {
    const response = await api.get('/admin/pms-csv-sources/value-sanitize-presets');
    return response.data;
  },
  parseSample: async (csvText) => {
    const response = await api.post('/admin/pms-csv-sources/parse-sample', { csvText });
    return response.data;
  },
  create: async (body) => {
    const response = await api.post('/admin/pms-csv-sources', body);
    return response.data;
  },
  patch: async (id, body) => {
    const response = await api.patch(`/admin/pms-csv-sources/${id}`, body);
    return response.data;
  },
  remove: async (id) => {
    await api.delete(`/admin/pms-csv-sources/${id}`);
  },
};

/** Admin: hotel_code + room_number → USD/guest-night infra overrides */
export const infraTaxRoomRulesAPI = {
  list: async () => {
    const response = await api.get('/admin/infra-tax-room-rules');
    return response.data;
  },
  create: async (body) => {
    const response = await api.post('/admin/infra-tax-room-rules', body);
    return response.data;
  },
  patch: async (id, body) => {
    const response = await api.patch(`/admin/infra-tax-room-rules/${id}`, body);
    return response.data;
  },
  remove: async (id) => {
    await api.delete(`/admin/infra-tax-room-rules/${id}`);
  },
};

/** SuperAdmin: hotel_code + month → prefer CSV for charts/VAT reports. */
export const sidebarMenuAPI = {
  get: async () => {
    const response = await api.get('/admin/sidebar-menu');
    return response.data;
  },
  update: async (body) => {
    const response = await api.put('/admin/sidebar-menu', body);
    return response.data;
  },
};

export const hotelMonthCsvOverridesAPI = {
  list: async (params = {}) => {
    const response = await api.get('/admin/hotel-month-csv-overrides', { params });
    return response.data;
  },
  create: async (body) => {
    const response = await api.post('/admin/hotel-month-csv-overrides', body);
    return response.data;
  },
  patch: async (id, body) => {
    const response = await api.patch(`/admin/hotel-month-csv-overrides/${id}`, body);
    return response.data;
  },
  remove: async (id) => {
    await api.delete(`/admin/hotel-month-csv-overrides/${id}`);
  },
};

/** SuperAdmin staging-only: pull PMS push package from production. */
export const pmsPushPackageAPI = {
  status: async () => {
    const response = await api.get('/admin/ops/pms-push-package/status');
    return response.data;
  },
  /** Prod push suppliers + hotels for admin dropdowns (proxied when import is enabled). */
  catalog: async (signal) => {
    const response = await api.get('/admin/ops/pms-push-package/catalog', { signal });
    return response.data;
  },
  pull: async (body) => {
    const response = await api.post('/admin/ops/pms-push-package/pull', body, {
      // Supplier-wide month sync can be large (100+ hotels).
      timeout: 20 * 60 * 1000,
    });
    return response.data;
  },
};

// Admin: CSV import hotels + import job rows (eZee manual export pipeline)
export const pmsCsvHotelsAPI = {
  /** Authenticated users (not admin-only); matches GET /pms-csv-hotels */
  list: async () => {
    const response = await api.get('/pms-csv-hotels');
    return response.data;
  },
  /** @param {{ year?: number, month?: number }} params — pass year+month (month 0–11) for Properties “single month” aggregates */
  revenueSummary: async (params = {}) => {
    const response = await api.get('/pms-csv-hotels/revenue-summary', { params });
    return response.data;
  },
  get: async (id) => {
    const response = await api.get(`/admin/pms-csv-hotels/${id}`);
    return response.data;
  },
  create: async (body) => {
    const response = await api.post('/admin/pms-csv-hotels', body);
    return response.data;
  },
  patch: async (id, body) => {
    const response = await api.patch(`/admin/pms-csv-hotels/${id}`, body);
    return response.data;
  },
  remove: async (id) => {
    await api.delete(`/admin/pms-csv-hotels/${id}`);
  },
  getRevenueReport: async (csvHotelId, year, monthOrOpts, maybeOpts) => {
    const opts =
      monthOrOpts && typeof monthOrOpts === 'object' && !Array.isArray(monthOrOpts)
        ? monthOrOpts
        : maybeOpts && typeof maybeOpts === 'object'
          ? maybeOpts
          : {};
    const month =
      monthOrOpts !== undefined && (typeof monthOrOpts === 'number' || typeof monthOrOpts === 'string')
        ? monthOrOpts
        : undefined;
    const params = new URLSearchParams();
    if (year != null) params.append('year', String(year));
    if (month !== undefined) params.append('month', String(month));
    const qs = params.toString();
    const response = await api.get(
      `/pms-csv-hotels/${csvHotelId}/reports/revenue${qs ? `?${qs}` : ''}`,
      { signal: opts.signal }
    );
    return response.data;
  },
  getVatReports: async (csvHotelId) => {
    const response = await api.get(`/pms-csv-hotels/${csvHotelId}/vat-reports`);
    return response.data;
  },
  deleteVatReport: async (csvHotelId, reportId) => {
    const response = await api.delete(`/pms-csv-hotels/${csvHotelId}/vat-reports/${reportId}`);
    return response.data;
  },
  getVatReportById: async (csvHotelId, reportId) => {
    const response = await api.get(`/pms-csv-hotels/${csvHotelId}/vat-reports/${reportId}?format=json`);
    return response.data;
  },
  generateVatReport: async (csvHotelId, year, month) => {
    const response = await api.post(
      `/pms-csv-hotels/${csvHotelId}/vat-reports/generate`,
      { year, month },
      { timeout: REPORT_GENERATE_TIMEOUT_MS }
    );
    return response.data;
  },
};

export const pmsCsvImportsAPI = {
  listByHotel: async (hotelId) => {
    const response = await api.get('/admin/pms-csv-imports', { params: { hotelId } });
    return response.data;
  },
  getById: async (id) => {
    const response = await api.get(`/admin/pms-csv-imports/by-id/${id}`);
    return response.data;
  },
  create: async (body) => {
    const response = await api.post('/admin/pms-csv-imports', body);
    return response.data;
  },
  patch: async (id, body) => {
    const response = await api.patch(`/admin/pms-csv-imports/${id}`, body);
    return response.data;
  },
  remove: async (id) => {
    await api.delete(`/admin/pms-csv-imports/${id}`);
  },
  /** Multipart upload: field name `file`. Parses CSV → pms_csv_bookings. */
  previewUploadCsv: async (importId, file) => {
    const form = new FormData();
    form.append('file', file);
    const response = await api.post(`/admin/pms-csv-imports/${importId}/upload-preview`, form, {
      timeout: 120000,
      transformRequest: [
        (data, headers) => {
          delete headers['Content-Type'];
          return data;
        },
      ],
    });
    return response.data;
  },
  /** Multipart upload: field name `file`. Parses CSV → pms_csv_bookings. */
  uploadCsv: async (importId, file, opts = {}) => {
    const form = new FormData();
    form.append('file', file);
    if (opts.ignoreUnknownColumns === true) {
      form.append('ignoreUnknownColumns', 'true');
    }
    if (opts.confirmMissingFinancialFields === true) {
      form.append('confirmMissingFinancialFields', 'true');
    }
    const response = await api.post(`/admin/pms-csv-imports/${importId}/upload`, form, {
      timeout: 120000,
      transformRequest: [
        (data, headers) => {
          delete headers['Content-Type'];
          return data;
        },
      ],
    });
    return response.data;
  },
};

/** Admin: rows in pms_csv_bookings (CSV import pipeline) */
export const pmsCsvBookingsAPI = {
  list: async (params = {}) => {
    const response = await api.get('/admin/pms-csv-bookings', { params });
    return response.data;
  },
};

// eZee Zanzibar push – API key management (Admin only)
export const ezeeZanzibarAPI = {
  getKeys: async () => {
    const response = await api.get('/admin/pms-zanzibar/keys');
    return response.data;
  },
  createKey: async (name = 'eZee Zanzibar', pmsSupplierId = undefined) => {
    const body = { name };
    if (pmsSupplierId !== undefined) body.pmsSupplierId = pmsSupplierId;
    const response = await api.post('/admin/pms-zanzibar/keys', body);
    return response.data;
  },
  updateKey: async (id, updates) => {
    const response = await api.patch(`/admin/pms-zanzibar/keys/${id}`, updates);
    return response.data;
  },
  getNormalizerStatus: async (params = {}) => {
    const response = await api.get('/admin/pms-zanzibar/normalizer-status', { params });
    return response.data;
  },
  getNormalizerRaw: async (rawId) => {
    const response = await api.get(`/admin/pms-zanzibar/normalizer-raw/${rawId}`);
    return response.data;
  },
  retryNormalizerError: async (rawId) => {
    const response = await api.post(`/admin/pms-zanzibar/normalizer-errors/${rawId}/retry`);
    return response.data;
  },
  getGatewayFailures: async (params = {}) => {
    const response = await api.get('/admin/pms-zanzibar/gateway-failures', { params });
    return response.data;
  },
  getGatewayFailure: async (id) => {
    const response = await api.get(`/admin/pms-zanzibar/gateway-failures/${id}`);
    return response.data;
  },
  retryGatewayFailure: async (id) => {
    const response = await api.post(`/admin/pms-zanzibar/gateway-failures/${id}/retry`);
    return response.data;
  },
  /** Replay open forward failures oldest-first (server batch). */
  retryAllGatewayFailures: async (body = {}) => {
    const response = await api.post('/admin/pms-zanzibar/gateway-failures/retry-all', body, {
      // Batches can take a while when sync is slow
      timeout: 300_000,
    });
    return response.data;
  },
  dismissGatewayFailure: async (id) => {
    const response = await api.post(`/admin/pms-zanzibar/gateway-failures/${id}/dismiss`);
    return response.data;
  },
  reprocessValidationGatewayFailures: async (body = {}) => {
    const response = await api.post(
      '/admin/pms-zanzibar/gateway-failures/reprocess-validation',
      body,
      { timeout: 300_000 }
    );
    return response.data;
  },
  dismissAllNonreplayableGatewayFailures: async (body = {}) => {
    const response = await api.post(
      '/admin/pms-zanzibar/gateway-failures/dismiss-all-nonreplayable',
      body,
      { timeout: 120_000 }
    );
    return response.data;
  },
};

// Admin: duplicate bookings / amount mismatches (canonical rows across providers)
export const adminBookingDiscrepanciesAPI = {
  listDuplicateGroups: async (params = {}) => {
    const response = await api.get('/admin/booking-discrepancies/duplicate-groups', { params });
    return response.data;
  },
  listNightsMismatch: async (params = {}) => {
    const response = await api.get('/admin/booking-discrepancies/nights-mismatch', { params });
    return response.data;
  },
  listDueAmountStale: async (params = {}) => {
    const response = await api.get('/admin/booking-discrepancies/due-amount-stale', { params });
    return response.data;
  },
  listStuckArrived: async (params = {}) => {
    const response = await api.get('/admin/booking-discrepancies/stuck-arrived', { params });
    return response.data;
  },
};

/** Admin (temporary): bookings with source payload revision rows */
export const adminBookingHistoryAPI = {
  listWithRevisions: async (params = {}) => {
    const response = await api.get('/admin/booking-history/with-revisions', { params });
    return response.data;
  },
};

// Audit API
export const auditAPI = {
  list: async (params = {}) => {
    const response = await api.get('/audit/logs', { params });
    return response.data;
  },
  trackPageVisit: async ({ path, title }) => {
    // Fire-and-forget callers may ignore failures.
    const response = await api.post('/audit/page-visit', { path, title });
    return response.data;
  },
  /** Server derives duration from JWT iat. */
  sessionEnd: async ({ reason } = {}) => {
    const response = await api.post('/audit/session-end', {
      reason: typeof reason === 'string' ? reason : undefined,
    });
    return response.data;
  },
};

/**
 * Best-effort session-end when the document unloads (tab close / navigate away).
 * Axios may be aborted; fetch + keepalive survives unload.
 */
export function postSessionEndKeepalive(reason = 'page_leave') {
  const token = typeof localStorage !== 'undefined' ? localStorage.getItem('authToken') : null;
  if (!token) return;
  const safeReason =
    typeof reason === 'string' && /^[a-zA-Z0-9_-]+$/.test(reason) ? reason.slice(0, 64) : 'page_leave';
  const url = `${String(API_BASE_URL).replace(/\/$/, '')}/audit/session-end`;
  try {
    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ reason: safeReason }),
      keepalive: true,
    }).catch(() => {});
  } catch {
    // ignore
  }
}

export const complianceAPI = {
  getTaskTypes: async () => {
    const response = await api.get('/compliance/task-types');
    return response.data;
  },
  getKpis: async () => {
    const response = await api.get('/compliance/kpis');
    return response.data;
  },
  getTaskStats: async () => {
    const response = await api.get('/compliance/task-stats');
    return response.data;
  },
  getProperties: async (params = {}) => {
    const response = await api.get('/compliance/properties', { params });
    return response.data;
  },
  getEntityOptions: async (params = {}) => {
    const response = await api.get('/compliance/entity-options', { params });
    return response.data;
  },
  getEntity: async (entityType, entityKey) => {
    const response = await api.get(
      `/compliance/entities/${encodeURIComponent(entityType)}/${encodeURIComponent(entityKey)}`
    );
    return response.data;
  },
  /** Active assignees for a responsible organization (`zra` | `hotelonline`). */
  assignees: async (organization) => {
    const response = await api.get('/compliance/assignees', {
      params: { organization },
    });
    return response.data;
  },
  listTasks: async (params = {}) => {
    const response = await api.get('/compliance/tasks', { params });
    return response.data;
  },
  getTask: async (id) => {
    const response = await api.get(`/compliance/tasks/${id}`);
    return response.data;
  },
  createTask: async (body) => {
    const response = await api.post('/compliance/tasks', body);
    return response.data;
  },
  updateTask: async (id, body) => {
    const response = await api.patch(`/compliance/tasks/${id}`, body);
    return response.data;
  },
  updateTaskChecklist: async (taskId, checklist) => {
    const response = await api.patch(`/compliance/tasks/${taskId}/checklist`, { checklist });
    return response.data;
  },
  cancelTask: async (id) => {
    const response = await api.delete(`/compliance/tasks/${id}`);
    return response.data;
  },
  addComment: async (taskId, body) => {
    const response = await api.post(`/compliance/tasks/${taskId}/comments`, body);
    return response.data;
  },
  uploadAttachment: async (taskId, file) => {
    const form = new FormData();
    form.append('file', file);
    const response = await api.post(`/compliance/tasks/${taskId}/attachments`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: REPORT_DOWNLOAD_TIMEOUT_MS,
    });
    return response.data;
  },
  downloadAttachment: async (taskId, attachmentId) => {
    const response = await api.get(`/compliance/tasks/${taskId}/attachments/${attachmentId}`, {
      responseType: 'blob',
    });
    return response.data;
  },
  listTemplates: async () => {
    const response = await api.get('/compliance/templates');
    return response.data;
  },
  suggestTemplate: async (taskType) => {
    const response = await api.get('/compliance/templates/suggest', { params: { taskType } });
    return response.data;
  },
  getTemplate: async (id) => {
    const response = await api.get(`/compliance/templates/${id}`);
    return response.data;
  },
  createTemplate: async (body) => {
    const response = await api.post('/compliance/templates', body);
    return response.data;
  },
  updateTemplate: async (id, body) => {
    const response = await api.patch(`/compliance/templates/${id}`, body);
    return response.data;
  },
  deleteTemplate: async (id) => {
    const response = await api.delete(`/compliance/templates/${id}`);
    return response.data;
  },
  listChecklistTemplates: async (params = {}) => {
    const response = await api.get('/compliance/checklist-templates', { params });
    return response.data;
  },
  getChecklistTemplate: async (id) => {
    const response = await api.get(`/compliance/checklist-templates/${id}`);
    return response.data;
  },
  createChecklistTemplate: async (body) => {
    const response = await api.post('/compliance/checklist-templates', body);
    return response.data;
  },
  updateChecklistTemplate: async (id, body) => {
    const response = await api.patch(`/compliance/checklist-templates/${id}`, body);
    return response.data;
  },
  deleteChecklistTemplate: async (id) => {
    const response = await api.delete(`/compliance/checklist-templates/${id}`);
    return response.data;
  },
  renderTemplate: async (id, vars) => {
    const response = await api.post(`/compliance/templates/${id}/render`, vars);
    return response.data;
  },
  exportTemplate: async (id, vars, format = 'pdf') => {
    let response;
    try {
      response = await api.post(
        `/compliance/templates/${id}/export`,
        { ...vars, format },
        { responseType: 'blob', timeout: REPORT_DOWNLOAD_TIMEOUT_MS }
      );
    } catch (err) {
      const blob = err.response?.data;
      if (blob instanceof Blob) {
        const text = await blob.text();
        try {
          const parsed = JSON.parse(text);
          throw new Error(parsed.message || 'Failed to export template');
        } catch (parseErr) {
          if (parseErr instanceof Error && parseErr.message !== 'Failed to export template') {
            throw parseErr;
          }
        }
      }
      throw err;
    }

    if (format === 'pdf') {
      return validateCompliancePdfBlob(response.data, response.headers?.['content-type']);
    }
    return response.data;
  },
  exportTaskPdf: async (taskId) => {
    let response;
    try {
      response = await api.get(`/compliance/tasks/${taskId}/export`, {
        params: { format: 'pdf' },
        responseType: 'blob',
        timeout: REPORT_DOWNLOAD_TIMEOUT_MS,
      });
    } catch (err) {
      const blob = err.response?.data;
      if (blob instanceof Blob) {
        const text = await blob.text();
        try {
          const parsed = JSON.parse(text);
          throw new Error(parsed.message || 'Failed to export task PDF');
        } catch (parseErr) {
          if (parseErr instanceof Error && parseErr.message !== 'Failed to export task PDF') {
            throw parseErr;
          }
        }
      }
      throw err;
    }

    return validateCompliancePdfBlob(response.data, response.headers?.['content-type']);
  },
};

async function validateCompliancePdfBlob(blob, contentType = '') {
  if (!contentType.includes('application/pdf')) {
    const text = await blob.text();
    try {
      const err = JSON.parse(text);
      throw new Error(err.message || 'Server returned non-PDF response');
    } catch (e) {
      if (e instanceof Error && e.message && !e.message.includes('JSON')) throw e;
      throw new Error('Server returned non-PDF response');
    }
  }
  if (!blob?.size) {
    throw new Error('Downloaded PDF is empty');
  }
  const header = await blob.slice(0, 4).text();
  if (header !== '%PDF') {
    throw new Error('Downloaded file is not a valid PDF');
  }
  return blob;
}

export const helpAPI = {
  getMeta: async () => {
    const response = await api.get('/help/meta');
    return response.data;
  },
  listArticles: async (params = {}) => {
    const response = await api.get('/help/articles', { params });
    return response.data;
  },
  getArticle: async (slug, params = {}) => {
    const response = await api.get(`/help/articles/${encodeURIComponent(slug)}`, { params });
    return response.data;
  },
  contextual: async (params = {}) => {
    const response = await api.get('/help/contextual', { params });
    return response.data;
  },
  submitFeedback: async (id, body) => {
    const response = await api.post(`/help/articles/${id}/feedback`, body);
    return response.data;
  },
  reportIssue: async (body) => {
    const response = await api.post('/help/issue-reports', body);
    return response.data;
  },
  manageCategories: async () => {
    const response = await api.get('/help/manage/categories');
    return response.data;
  },
  createCategory: async (body) => {
    const response = await api.post('/help/manage/categories', body);
    return response.data;
  },
  updateCategory: async (id, body) => {
    const response = await api.put(`/help/manage/categories/${id}`, body);
    return response.data;
  },
  deleteCategory: async (id) => {
    const response = await api.delete(`/help/manage/categories/${id}`);
    return response.data;
  },
  manageArticles: async (params = {}) => {
    const response = await api.get('/help/manage/articles', { params });
    return response.data;
  },
  createArticle: async (body) => {
    const response = await api.post('/help/manage/articles', body);
    return response.data;
  },
  updateArticle: async (id, body) => {
    const response = await api.put(`/help/manage/articles/${id}`, body);
    return response.data;
  },
  publishArticle: async (id) => {
    const response = await api.post(`/help/manage/articles/${id}/publish`);
    return response.data;
  },
  unpublishArticle: async (id) => {
    const response = await api.post(`/help/manage/articles/${id}/unpublish`);
    return response.data;
  },
  archiveArticle: async (id) => {
    const response = await api.post(`/help/manage/articles/${id}/archive`);
    return response.data;
  },
  upsertTranslation: async (id, locale, body) => {
    const response = await api.put(
      `/help/manage/articles/${id}/translations/${encodeURIComponent(locale)}`,
      body
    );
    return response.data;
  },
  uploadAttachment: async (id, file) => {
    const form = new FormData();
    form.append('file', file);
    const response = await api.post(`/help/manage/articles/${id}/attachments`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  deleteAttachment: async (articleId, attachmentId) => {
    const response = await api.delete(
      `/help/manage/articles/${articleId}/attachments/${attachmentId}`
    );
    return response.data;
  },
};

export default api;


